/* eslint-disable max-lines-per-function */
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment } from 'react';
import { StyleProps } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/types';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@e2n/ui';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Location } from '@/domain/mitarbeiter/features/rotationsplan/components/Location';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { KalenderByArbeitsbereicheStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/kalenderByArbeitsbereiche/KalenderStation';
import { KalenderArbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/kalenderByArbeitsbereiche/KalenderArbeitsbereich';
import { useHoverPopover } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/useHoverPopover';

export function ArbeitsbereichKalenderStation({
  kalenderStation,
  arbeitsbereich,
  displayYear,
}: {
  kalenderStation: KalenderByArbeitsbereicheStation;
  arbeitsbereich: KalenderArbeitsbereich;
  displayYear: number;
}) {
  const { ref, open, setOpen, cursorX, handleMouseEnter, handleMouseLeave } =
    useHoverPopover();
  const { t } = useTranslation();

  return (
    <div
      className={`col-start-(--col-start) col-end-(--col-end) py-1`}
      style={
        {
          '--col-start': kalenderStation.getColStart(displayYear),
          '--col-end': kalenderStation.getColEnd(displayYear),
        } as StyleProps
      }>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="relative w-full h-full"
        ref={ref}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            style={{
              position: 'absolute',
              left: cursorX,
              bottom: 0,
              height: 0.5,
              width: 0.5,
              color: 'white',
              outline: 'none',
            }}
          />
          <div
            className={`w-full h-full bg-(--bg-color) border-(--border-color) border border-solid rounded-md flex items-center justify-start p-2`}
            style={
              {
                '--bg-color': arbeitsbereich.getFillColor(),
                '--border-color': arbeitsbereich.getBorderColor(),
              } as StyleProps
            }>
            <div className="text-xs truncate">
              {kalenderStation.mitarbeiter.length}
            </div>
          </div>
          <PopoverContent
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="bg-white flex flex-col gap-2 p-3 text-sm w-75">
            <div className="flex flex-col mb-2 gap-1">
              <div className="flex gap-2 items-center">
                <FontAwesomeIcon
                  icon={faCircle}
                  color={arbeitsbereich.getBorderColor()}
                  className='text-xs'
                />
                <div className="font-semibold">{arbeitsbereich.name}</div>
              </div>
              <div className="text-xs text-neutrals-800">
                {dayjs(kalenderStation.station.von).format('DD.MM.YYYY') +
                  '–' +
                  dayjs(kalenderStation.station.bis).format('DD.MM.YYYY') +
                  ' ⋅ ' +
                  kalenderStation.mitarbeiter.length +
                  ' ' +
                  t('entities.employee')}
              </div>
            </div>
            {kalenderStation.mitarbeiter.map((m, index) => {
              return (
                <Fragment key={m.id}>
                  <div key={m.id} className="flex gap-2 items-center">
                    <Avatar>
                      <AvatarImage
                        alt={m.vorname + ' ' + m.nachname}
                        src={m.imgUrl}
                      />
                      <AvatarFallback>
                        {m.vorname[0] + m.nachname[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:flex md:flex-col md:gap-1 min-w-0">
                      <div className="text-sm text-neutrals-900 truncate">
                        {m.vorname + ' ' + m.nachname}
                      </div>
                      <div className="text-xs text-neutrals-700">
                        {m.lehrjahr + ' ⋅ ' + m.beruf.abkuerzung}
                      </div>
                      {kalenderStation.station.abteilungsleiter && (
                        <div className="flex items-center gap-1 text-neutrals-700 text-xs">
                          <FontAwesomeIcon icon={faUser} />
                          {kalenderStation.station.abteilungsleiter.getName()}
                        </div>
                      )}
                      <Location
                        standort={
                          kalenderStation.station.arbeitsbereich.standort
                        }
                      />
                    </div>
                  </div>
                  {index < kalenderStation.mitarbeiter.length - 1 && (
                    <div className="bg-neutrals-300 w-full h-px" />
                  )}
                </Fragment>
              );
            })}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
